/* client/src/services/binanceService.ts */
// Secure Binance API service for client-side
class BinanceService {
  private apiKey: string;
  private secretKey: string;
  private baseURL: string = 'https://api.binance.com';

  constructor() {
    this.apiKey = process.env.REACT_APP_BINANCE_API_KEY || '';
    this.secretKey = process.env.REACT_APP_BINANCE_SECRET_KEY || '';
  }

  // Generate signature for authenticated requests
  private generateSignature(queryString: string): string {
    const crypto = require('crypto-js');
    return crypto.HmacSHA256(queryString, this.secretKey).toString();
  }

  // Get current gold price (PAXG/USDT)
  async getGoldPrice(): Promise<{ price: number; symbol: string; timestamp: number }> {
    try {
      const response = await fetch(`${this.baseURL}/api/v3/ticker/price?symbol=PAXGUSDT`);
      
      if (!response.ok) {
        throw new Error(`Binance API error: ${response.status}`);
      }
      
      const data = await response.json();
      return {
        price: parseFloat(data.price),
        symbol: data.symbol,
        timestamp: Date.now()
      };
    } catch (error) {
      console.error('Binance API failed:', error);
      throw error;
    }
  }

  // Get gold price with authentication (if needed)
  async getAuthenticatedGoldPrice() {
    if (!this.apiKey || !this.secretKey) {
      return await this.getGoldPrice(); // Fallback to public endpoint
    }

    try {
      const timestamp = Date.now();
      const queryString = `symbol=PAXGUSDT&timestamp=${timestamp}`;
      const signature = this.generateSignature(queryString);
      
      const response = await fetch(
        `${this.baseURL}/api/v3/ticker/price?${queryString}&signature=${signature}`,
        {
          headers: {
            'X-MBX-APIKEY': this.apiKey,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error(`Authenticated Binance API error: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Authenticated Binance API failed, falling back to public endpoint:', error);
      return await this.getGoldPrice();
    }
  }

  // Get 24-hour gold price history for chart
  async getGoldPriceHistory(limit: number = 24): Promise<Array<{time: number; price: number}>> {
    try {
      const response = await fetch(
        `${this.baseURL}/api/v3/klines?symbol=PAXGUSDT&interval=1h&limit=${limit}`
      );
      
      if (!response.ok) {
        throw new Error(`Binance history API error: ${response.status}`);
      }
      
      const data = await response.json();
      return data.map((kline: any[]) => ({
        time: kline[0],
        price: parseFloat(kline[4]) // Closing price
      }));
    } catch (error) {
      console.error('Failed to fetch gold price history:', error);
      return [];
    }
  }
}

export const binanceService = new BinanceService();